import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { FolderItem } from 'src/app/api/services/folder';
import {
  StorageFile,
  StorageFilesDetails,
} from 'src/app/storage/services/storage-utils.service';

export type ListItem<T extends { folderId?: string }> =
  | {
      item: T;
      isFolder: false;
      selected: boolean;
    }
  | {
      item: FolderItem;
      isFolder: true;
      selected: boolean;
    };

export type ListItemKind =
  | 'folder'
  | 'Display'
  | 'Schedule'
  | 'Presentation'
  | 'file';

const DEFAULT_FILES_DETAILS: StorageFilesDetails = {
  code: 202,
  bucketExists: false,
};

@Injectable()
export class ScrollingListStateService<T extends { folderId?: string }> {
  private readonly list$ = new BehaviorSubject<ListItem<T | StorageFile>[]>([]);
  private readonly error$ = new BehaviorSubject<{
    apiError: string;
    errorMessage: string;
    errorCategory?: string;
  } | null>(null);
  public readonly maxCount = 40;
  private name: string = '';
  private cursor: string | null = null;
  private folderCursor: string | null = null;
  private itemsEnded = false;
  /**
   * Starts as `true` when the list has no folders (storage, assigned
   * publishers), so `isEndOfList()` is driven by the items cursor alone.
   */
  private foldersEnded = false;
  private foldersEnabled = true;
  private allSelected = false;
  private readonly loadingItems$ = new BehaviorSubject<boolean>(false);
  private _filesDetails: StorageFilesDetails;
  get filesDetails(): StorageFilesDetails {
    return this._filesDetails || DEFAULT_FILES_DETAILS;
  }
  set filesDetails(value: StorageFilesDetails) {
    this._filesDetails = value;
  }

  list() {
    return this.list$.asObservable();
  }

  add(items: ListItem<T | StorageFile>[], cursor: string | null) {
    this.list$.next(this.sortFoldersFirst([...this.list$.value, ...items]));
    this.cursor = cursor;
    this.itemsEnded = !cursor;
  }

  /**
   * Adds a page of folders. Folders paginate on their own cursor, so this does
   * not touch the items cursor.
   */
  addFolders(
    folders: ListItem<T | StorageFile>[],
    folderCursor: string | null,
  ) {
    this.list$.next(this.sortFoldersFirst([...this.list$.value, ...folders]));
    this.folderCursor = folderCursor;
    this.foldersEnded = !folderCursor;
  }

  update(items: ListItem<T | StorageFile>[], cursor: string | null) {
    this.list$.next(this.sortFoldersFirst(items));
    this.cursor = cursor;
    this.itemsEnded = items.length < this.maxCount;
  }

  isEndOfList() {
    return this.foldersEnded && this.itemsEnded;
  }

  isFoldersEnded() {
    return this.foldersEnded;
  }

  isItemsEnded() {
    return this.itemsEnded;
  }

  currentFolderCursor() {
    return this.folderCursor;
  }

  /**
   * Lists without folders (storage, assigned publishers) must report folders as
   * already exhausted, otherwise `isEndOfList()` could never become true.
   */
  setFoldersEnabled(enabled: boolean) {
    this.foldersEnabled = enabled;
    if (!enabled) {
      this.foldersEnded = true;
    }
  }

  clear() {
    this.list$.next([]);
    this.cursor = null;
    this.folderCursor = null;
    this.itemsEnded = false;
    this.foldersEnded = !this.foldersEnabled;
    this.allSelected = false;
    this._filesDetails = undefined;
  }

  loadingItems() {
    return this.loadingItems$.asObservable();
  }

  isLoadingItems() {
    return this.loadingItems$.value;
  }

  setLoadingItems(loading: boolean) {
    this.loadingItems$.next(loading);
  }

  currentCursor() {
    return this.cursor;
  }

  isFirstPage() {
    return this.cursor === null && this.folderCursor === null;
  }

  currentList() {
    return this.list$.value;
  }

  selectAll() {
    const list = this.list$.value;
    list.forEach((item) => {
      item.selected = true;
    });
    this.list$.next([...list]);
    this.allSelected = true;
  }

  deselectAll() {
    const list = this.list$.value;
    list.forEach((item) => {
      item.selected = false;
    });

    this.list$.next([...list]);
    this.allSelected = false;
  }

  select(item: ListItem<T | StorageFile>, selected?: true) {
    item.selected = selected !== undefined ? selected : !item.selected;
    this.allSelected = this.list$.value.every((item) => item.selected);
  }

  deselect(item: ListItem<T | StorageFile>) {
    item.selected = false;
    this.allSelected = this.list$.value.every((item) => item.selected);
  }

  isAllSelected() {
    return this.allSelected;
  }

  isAllUnselected() {
    return this.list$.value.every((item) => !item.selected);
  }

  isAnySelected() {
    return this.list$.value.some((item) => item.selected);
  }

  getSelected = () => {
    return this.list$.value.filter((item) => item.selected);
  };

  setError(error: {
    apiError: string;
    errorMessage: string;
    errorCategory?: string;
  }) {
    this.error$.next(error);
  }

  error() {
    return this.error$.asObservable();
  }

  currentError() {
    return this.error$.value;
  }

  setName(name: string) {
    this.name = name;
  }

  getName() {
    return this.name;
  }

  remove(item: ListItem<T | StorageFile>) {
    this.list$.next(this.list$.value.filter((i) => i !== item));
  }

  clearMessages() {
    this.loadingItems$.next(false);
    this.error$.next(null);
  }

  private sortFoldersFirst(list: ListItem<T | StorageFile>[]) {
    return list.sort((a, b) => {
      if (a.isFolder && !b.isFolder) return -1;
      if (!a.isFolder && b.isFolder) return 1;
      return 0;
    });
  }
}
